import React, { useMemo, useState } from "react";
import {
  CROP_PREP_PRESETS,
  CROP_ZOOM_MAX,
  CROP_ZOOM_MIN,
  clampCropZoom,
  clampCustomRatioPart,
} from "../../../lib/cropPrepModel";
import "./CropPrepControls.css";

const CropPrepControls = ({
  settings,
  onChange = () => {},
  disabled = false,
}) => {
  const [ratioDraft, setRatioDraft] = useState({ w: null, h: null });
  const [zoomDraft, setZoomDraft] = useState(null);
  const preset = settings?.preset || "freeform";
  const zoom = clampCropZoom(settings?.zoom);
  const zoomInputValue = zoomDraft != null ? zoomDraft : String(zoom);
  const customRatioW =
    ratioDraft.w != null ? ratioDraft.w : String(settings?.customRatioW ?? 1);
  const customRatioH =
    ratioDraft.h != null ? ratioDraft.h : String(settings?.customRatioH ?? 1);
  const sliderFillPct = useMemo(() => {
    const span = CROP_ZOOM_MAX - CROP_ZOOM_MIN;
    if (!span) return 0;
    return Math.max(0, Math.min(100, ((zoom - CROP_ZOOM_MIN) / span) * 100));
  }, [zoom]);

  const patch = (partial) => onChange({ ...settings, ...partial });

  const setPreset = (nextPreset) => {
    patch({ preset: nextPreset });
  };

  const commitRatioPart = (key, raw) => {
    const parsed = clampCustomRatioPart(raw, key === "customRatioW" ? 1 : 1);
    patch({
      preset: "custom",
      [key]: parsed,
    });
    setRatioDraft((prev) => ({ ...prev, [key === "customRatioW" ? "w" : "h"]: null }));
  };

  const commitZoomDraft = () => {
    const raw = zoomDraft != null ? zoomDraft : String(zoom);
    patch({
      zoom: clampCropZoom(raw === "" ? CROP_ZOOM_MIN : raw),
    });
    setZoomDraft(null);
  };

  return (
    <div className="crop-prep-controls">
      <div className="crop-prep-controls__section">
        <span className="crop-prep-controls__section-title">Crop Presets</span>
        <div className="crop-prep-controls__preset-grid">
          {CROP_PREP_PRESETS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`crop-prep-controls__preset${
                preset === opt.id ? " is-active" : ""
              }`}
              disabled={disabled}
              aria-pressed={preset === opt.id}
              onClick={() => setPreset(opt.id)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {preset === "custom" ? (
        <div className="crop-prep-controls__section">
          <span className="crop-prep-controls__section-title">Custom Ratio</span>
          <div className="crop-prep-controls__ratio-row">
            <input
              type="text"
              inputMode="numeric"
              className="crop-prep-controls__ratio-input"
              value={customRatioW}
              disabled={disabled}
              aria-label="Custom ratio width"
              onChange={(e) => {
                setRatioDraft((prev) => ({ ...prev, w: e.target.value }));
              }}
              onBlur={() => commitRatioPart("customRatioW", customRatioW)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRatioPart("customRatioW", customRatioW);
              }}
            />
            <span className="crop-prep-controls__ratio-sep" aria-hidden="true">
              ×
            </span>
            <input
              type="text"
              inputMode="numeric"
              className="crop-prep-controls__ratio-input"
              value={customRatioH}
              disabled={disabled}
              aria-label="Custom ratio height"
              onChange={(e) => {
                setRatioDraft((prev) => ({ ...prev, h: e.target.value }));
              }}
              onBlur={() => commitRatioPart("customRatioH", customRatioH)}
              onKeyDown={(e) => {
                if (e.key === "Enter") commitRatioPart("customRatioH", customRatioH);
              }}
            />
          </div>
        </div>
      ) : null}

      <div className="crop-prep-controls__section">
        <div className="crop-prep-controls__slider-head">
          <span className="crop-prep-controls__section-title">Zoom</span>
          <div className="crop-prep-controls__slider-value">
            <input
              type="text"
              inputMode="numeric"
              className="crop-prep-controls__slider-input"
              value={zoomInputValue}
              disabled={disabled}
              aria-label="Zoom percentage"
              onChange={(e) => {
                setZoomDraft(e.target.value.replace(/[^0-9]/g, "").slice(0, 3));
              }}
              onBlur={commitZoomDraft}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  commitZoomDraft();
                  e.currentTarget.blur();
                }
              }}
            />
            <span className="crop-prep-controls__slider-suffix">%</span>
          </div>
        </div>
        <input
          type="range"
          className="crop-prep-controls__slider"
          min={CROP_ZOOM_MIN}
          max={CROP_ZOOM_MAX}
          step={1}
          value={zoom}
          disabled={disabled}
          aria-label="Zoom slider"
          style={{ "--crop-slider-fill": `${sliderFillPct}%` }}
          onChange={(e) => {
            setZoomDraft(null);
            patch({ zoom: clampCropZoom(e.target.value) });
          }}
        />
      </div>
    </div>
  );
};

export default CropPrepControls;
